import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Grid, Card, CardContent, Typography, CircularProgress, Alert } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuizIcon from "@mui/icons-material/Quiz";
import WarningIcon from "@mui/icons-material/Warning";
import VideocamIcon from "@mui/icons-material/Videocam";
import api from "../../api";
import AnnouncementsList from "../../components/AnnouncementsList";

const StatCard = ({ title, value, icon, color, to }) => (
  <Card
    component={to ? RouterLink : Box}
    to={to}
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`,
      color: "#fff",
      textDecoration: "none",
      display: "block",
      transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      "&:hover": to ? {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        opacity: 0.95
      } : {}
    }}
  >
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography color="inherit" variant="body2" fontWeight={600} sx={{ opacity: 0.9, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ opacity: 0.8 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const TutorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urgentAnnouncements, setUrgentAnnouncements] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get("tutor/dashboard");
        if (res.data?.success) setData(res.data.data);
        else setError("Failed to load dashboard");
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const fetchUrgentAnnouncements = async () => {
      try {
        const response = await api.get("tutor/announcements", { params: { per_page: 100 } });
        const raw = response.data?.data;
        const list = Array.isArray(raw) ? raw : (raw?.data ? raw.data : []);
        const now = new Date();
        const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const urgent = list.filter((a) => {
          if ((a.priority_level || "").toLowerCase() !== "urgent") return false;
          const created = a.created_at ? new Date(a.created_at) : null;
          return created && created >= cutoff;
        });
        setUrgentAnnouncements(urgent);
      } catch (err) {
        console.error("Error fetching urgent announcements:", err);
        setUrgentAnnouncements([]);
      }
    };
    fetchUrgentAnnouncements();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  const d = data || {};
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {urgentAnnouncements.length > 0 && (
        <Alert
          severity="error"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {urgentAnnouncements.map((ann) => (
              <Box
                key={ann.id}
                component={RouterLink}
                to="/tutor/announcements"
                sx={{
                  py: 1,
                  px: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 1,
                  borderLeft: "4px solid #d32f2f",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  display: "block",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {ann.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {ann.message?.length > 200 ? ann.message.slice(0, 200) + "…" : ann.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      {/* Announcements */}
      <AnnouncementsList role="tutor" />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Courses"
            value={d.courses_count ?? 0}
            icon={<SchoolIcon sx={{ fontSize: 48 }} />}
            color="#3B2A9F"
            to="/tutor/courses"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Students"
            value={d.students_count ?? 0}
            icon={<PeopleIcon sx={{ fontSize: 48 }} />}
            color="#667eea"
            to="/tutor/students"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Scheduled Classes"
            value={d.scheduled_classes_count ?? 0}
            icon={<VideocamIcon sx={{ fontSize: 48 }} />}
            color="#ec4899"
            to="/tutor/classes?tab=2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Assignments"
            value={d.assignments_count ?? 0}
            icon={<AssignmentIcon sx={{ fontSize: 48 }} />}
            color="#764ba2"
            to="/tutor/assignments"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Timeslots"
            value={d.timeslots_count ?? 0}
            icon={<AccessTimeIcon sx={{ fontSize: 48 }} />}
            color="#D62926"
            to="/tutor/timeslots"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Tests"
            value={d.tests_count ?? 0}
            icon={<QuizIcon sx={{ fontSize: 48 }} />}
            color="#0ea5e9"
            to="/tutor/tests"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TutorDashboard;
